import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DataTableColumn } from '@common/components/datatable/types';
import { RecognitionObject, RecognitionValue } from './types';
import Profile from '@common/components/profile';
import { LoggedUser } from '@common/types/objects';
import Tooltip from '@common/components/tooltip';
import Badge from '@common/components/badge';
import { Trans, useTranslation } from 'react-i18next';
import BadgeCount from '@common/components/badgeCount';
import LinkWrapper from '@common/components/linkWrapper';
import { useParams } from 'react-router';

export default (
  data: RecognitionObject[],
  meta: any,
  loggedUser: LoggedUser,
  isNetworkView: boolean
) => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<DataTableColumn[]>([]);
  const { networkId } = useParams<{ networkId: string }>();
  const baseUrl = networkId ? `/networks/${networkId}` : '/admin';
  useEffect(() => {
    const baseColumns: DataTableColumn[] = [
      {
        key: 'sender_id',
        label: t('organisation:high_fives_sender'),
        options: {
          size: 80,
          render: (row: RecognitionObject) => {
            const sender = row.sender_id;
            if (!sender) return null;
            return (
              <LinkWrapper
                url={`${baseUrl}/users/${sender.id}/about`}
                className="tw-flex tw-flex-col [&_h5]:!tw-pl-2 tw-text-inherit hover:tw-text-inherit hover:tw-underline"
              >
                <Profile
                  user={sender}
                  enablePopover={sender.id !== loggedUser.id}
                />
              </LinkWrapper>
            );
          },
        },
      },

      {
        key: 'recipient_id',
        label: t('organisation:high_fives_receiver'),
        options: {
          size: 80,
          render: (row: RecognitionObject) => {
            const receiver = row.recipient_id;
            if (!receiver) return null;
            return (
              <LinkWrapper
                url={`${baseUrl}/users/${receiver.id}/about`}
                className="tw-flex tw-flex-col [&_h5]:!tw-pl-2 tw-text-inherit hover:tw-text-inherit hover:tw-underline"
              >
                <Profile
                  user={receiver}
                  enablePopover={receiver.id !== loggedUser.id}
                />
              </LinkWrapper>
            );
          },
        },
      },
      {
        key: 'text',
        label: t('organisation:high_fives_message'),
        options: {
          size: 200,
          sortable: true,
          align: 'left',
          render: (row: RecognitionObject) => (
            <Tooltip fullTitle title={row.text} placement="bottom">
              <div className="tw-line-clamp-2 tw-break-words">{row.text}</div>
            </Tooltip>
          ),
        },
      },
      {
        key: 'values',
        label: t('organisation:high_fives_values'),
        options: {
          size: 100,
          renderLite: (value: RecognitionValue[]) => {
            return value.map((val: RecognitionValue) => (
              <Tooltip
                key={val.id}
                fullTitle
                title={val.description ? val.description : ''}
                placement="top"
              >
                <div className="tw-flex tw-whitespace-nowrap">
                  <Badge text={val.name} />
                </div>
              </Tooltip>
            ));
          },
        },
      },
    ];

    // Add network and function columns only when not in network view
    if (!isNetworkView) {
      baseColumns.push(
        {
          key: 'network',
          label: t('organisation:high_fives_network'),
          options: {
            size: 150,
            render: (row: RecognitionObject) => {
              return row.recipient_id.networks &&
                row.recipient_id.networks.length > 0 ? (
                  <div>
                    {row.recipient_id.networks[0]}{' '}
                    {row.recipient_id.networks.length > 1 && (
                      <BadgeCount
                        title={row.recipient_id.networks.map((network) => (
                          <div>{network}</div>
                        ))}
                        count={row.recipient_id.networks.length - 1}
                      />
                    )}
                  </div>
                ) : (
                  <small>
                    <Trans i18nKey="organisation:users_email_empty" />
                  </small>
                );
            },
          },
        },
        {
          key: 'function',
          label: t('organisation:high_fives_function'),
          options: {
            size: 150,
            render: (row: RecognitionObject) => {
              return row.recipient_id.functions &&
                row.recipient_id.functions.length > 0 ? (
                  <div>
                    {row.recipient_id.functions[0]}{' '}
                    {row.recipient_id.functions.length > 1 && (
                      <BadgeCount
                        title={row.recipient_id.functions.map((func) => (
                          <div>{func}</div>
                        ))}
                        count={row.recipient_id.functions.length - 1}
                      />
                    )}
                  </div>
                ) : (
                  <small>
                    <Trans i18nKey="organisation:users_email_empty" />
                  </small>
                );
            },
          },
        }
      );
    }

    baseColumns.push({
      key: 'created_at',
      label: t('organisation:high_fives_created_at'),
      options: {
        size: 80,
        sortable: true,
        render: (row: RecognitionObject) => (
          <Tooltip
            fullTitle
            title={moment(row.created_at).format('MMMM D, YYYY HH:mm:ss')}
            placement="bottom"
          >
            <div>{moment(row.created_at).format('MMM D, YYYY')}</div>
          </Tooltip>
        ),
      },
    });

    setColumns(baseColumns);
  }, [data, meta, loggedUser, t, isNetworkView]);

  return columns;
};
